import React, { lazy, Suspense, createContext } from "react";

import { Route, Routes } from "react-router-dom";

import ErrorBoundary from "../generic/error-boundary/error-boundary";
import LoadingSpinner from "../generic/loading/content-loading-spinner/content-loading-spinner";

const AppRoutePropertiesContext = createContext({});

// #region code split component loads
const NotFoundPage = lazy(() => import("../pages/errors/not-found-page/not-found-page.js"));
const HomePage = lazy(() => import("../pages/home/home-page.js"));
const YearPage = lazy(() => import("../pages/year/year-page/year-page.js"));
const AddYearPage = lazy(() => import("../pages/year/add-year-page/add-year-page"));
const HelpCenterPage = lazy(() => import("../pages/help-center/help-center-page"));
const UserAlertsPage = lazy(() => import("../pages/user-alerts/user-alerts-page"));
const SystemAlertsPage = lazy(() => import("../pages/admin/system-alerts/system-alerts-page"));
const StudentSearchPage = lazy(() => import("../pages/students/student-search-page/student-search-page"));
const StudentProfilePage = lazy(() => import("../pages/students/student-profile-page/student-profile-page"));
const MergeStudentsPage = lazy(() => import("../pages/students/merge-students-page/merge-students-page"));
const NewStudentPage = lazy(() => import("../pages/students/new-student-page/new-student-page"));
const EditSeminaryContactPage = lazy(() => import("../pages/students/edit-seminary-contact-page/edit-seminary-contact-page"));
const AddSeminaryRecordAccessPage = lazy(() =>
  import("../pages/students/add-seminary-record-access-page/add-seminary-record-access-page"));
const ProgramPage = lazy(() => import("../pages/programs/program-page/program-page"));
const BuildingsPage = lazy(() => import("../pages/programs/buildings-page/buildings-page"));
const CreateBuildingPage = lazy(() => import("../pages/programs/create-building-page/create-building-page"));
const ProgramBuildingsPage = lazy(() => import("../pages/programs/program-buildings-page/program-buildings-page"));
const FacultyPage = lazy(() => import("../pages/programs/faculty-page/faculty-page"));
const FacultyDetailPage = lazy(() => import("../pages/programs/faculty-detail-page/faculty-detail-page"));
const PeriodsPage = lazy(() => import("../pages/programs/periods-page/periods-page"));
const ClassListPage = lazy(() => import("../pages/classes/class-list-page.js"));
const ClassPage = lazy(() => import("../pages/class/class-page.js"));
const SisClassPage = lazy(() => import("../pages/sis/sis-class/sis-class-page.js"));
const AddClassPage = lazy(() => import("../pages/create-class/create-class-page.js"));
const SisCreateClassPage = lazy(() => import("../pages/sis/sis-create-class/sis-create-class-page.js"));
const MoveClassPage = lazy(() => import("../pages/move-class/move-class-page.js"));
const CloseTermPage = lazy(() => import("../pages/terms/close-page.js"));
const AutoSchedulerPage = lazy(() => import("../pages/terms/auto-scheduler-page/auto-scheduler-page"));
const PrintRolls = lazy(() => import("../pages/terms/auto-scheduler-page/print-page.js"));
const TemporaryFacultyPage = lazy(() => import("../pages/programs/temporary-faculty/temporary-faculty-detail.js"));
const EmployeeDirectoryPage = lazy(() => import("../pages/employee-directory/employee-directory-page"));
const GraduationStakesPage = lazy(() => import("../pages/graduation/stakes-page"));
const MessagingScreen = lazy(() => import("../pages/messaging/messaging-page"));
const HubsPage = lazy(() => import ("../pages/programs/hubs-page/hubs-page"));
const ReportsPage = lazy(() => import("../pages/reports/reports-page"));

const CourseManagmentPage = lazy(() => import("../pages/admin/course-management/course-management-page"));

// #endregion

const AppRoutes = ({
  alerts,
  warnedOn,
  warnedOff,
  printOn,
  requestLanguageGroups,
  requestPermissions,
  analyticsPageEvent,
  suppressNotFound = false
}) => {
  const areaPath = "areas/:areaId";
  const regionPath = `${areaPath}/regions/:regionId`;
  const coordinatorPath = `${regionPath}/coordinators/:coordinatorId`;
  const programPath = `${coordinatorPath}/programs/:programId`;
  const yearPath = `${programPath}/years/:yearId`;
  const termPath = `${yearPath}/terms/:termId`;

  const routeProperties = {requestLanguageGroups,requestPermissions, warnedOn, warnedOff, printOn, analyticsPageEvent};
  return (
    <Suspense fallback={<LoadingSpinner large={true} smartHideText={true}/>}>
      <AppRoutePropertiesContext.Provider value={routeProperties}>
        <ErrorBoundary>
          <Routes>
            {/*Term routes*/}
            <Route path={termPath}>
              <Route path={`/${termPath}`} element ={<HomePage/>}/>
              <Route path="new-student" element = {<NewStudentPage/>}/>
              <Route path="classes/:classId/new-student" element={<NewStudentPage />} />
              <Route path="sis/classes/:classId/new-student" element={<NewStudentPage />} />
              <Route path="addClass" element={<AddClassPage/>}/>
              <Route path="classes" element={<ClassListPage/>}/>
              <Route path="classes/:classId" element={<ClassPage />} />
              <Route path="sis/classes/:classId" element={<SisClassPage />} />
              <Route path="sis/addClass" element={<SisCreateClassPage/>}/>
              <Route path="classes/:classId/moveclass" element={<MoveClassPage />} />
              <Route path="classes/:classId/students/:studentId" element={<StudentProfilePage/>}/>
              <Route path="sis/classes/:classId/students/:studentId" element={<StudentProfilePage/>}/>
              <Route path="closeTerm" element={<CloseTermPage/>} />
              <Route path="autoScheduler" element={<AutoSchedulerPage/>} />
              <Route path="autoScheduler/print" element={<PrintRolls/>} />
              <Route path="merge-students" element={<MergeStudentsPage />} />
              <Route path="students" element={<StudentSearchPage />} />
              <Route path="students/:studentId" element={<StudentProfilePage />} />
              <Route path="students/:studentId/seminary-contacts/:contactId" element={<EditSeminaryContactPage/>}/>
              <Route path="students/:studentId/new-seminary-contact" element={<AddSeminaryRecordAccessPage/>}/>
              <Route path="graduation/stakes/*" element={<GraduationStakesPage/>}/>
              <Route path="messaging" element={<MessagingScreen/>}/>
              <Route path="reports" element={<ReportsPage/>}/>
            </Route>
            {/*Year Routes*/}
            <Route path={yearPath}>
              <Route path={`/${yearPath}`} element={<HomePage/>}/>
              <Route path="info" element={<YearPage/>}/>
            </Route>
            {/*Program Routes */}
            <Route path={programPath}>
              <Route path={`/${programPath}`} element={<HomePage/>}/>
              <Route path="info" element={<ProgramPage/>}/>
              <Route path="periods" element={<PeriodsPage/>}/>
              <Route path="buildings" element={<ProgramBuildingsPage/>}/>
              <Route path="buildings/:buildingId" element={<BuildingsPage/>}/>
              <Route path="addbuilding" element={<CreateBuildingPage/>}/>
              <Route path="faculty" element={<FacultyPage/>}/>
              <Route path="faculty/:employeeId" element={<FacultyDetailPage/>}/>
              <Route path="temporary-faculty/:employeeId" element={<TemporaryFacultyPage/>}/>
              <Route path="addyear" element={<AddYearPage/>}/>
              <Route path="employee-directory" element={<EmployeeDirectoryPage />} />
            </Route>
            {/*Coordinator Routes*/}
            <Route path={coordinatorPath}>
              <Route path={`/${coordinatorPath}`} element={<HomePage/>}/>
              <Route path="info" element={<ProgramPage/>}/>
              <Route path="faculty" element={<FacultyPage/>}/>
              <Route path="faculty/:employeeId" element={<FacultyDetailPage/>}/>
              <Route path="employee-directory" element={<EmployeeDirectoryPage/>}/>
              <Route path="institute-hubs" element={<HubsPage/>}/>
            </Route>
            {/*Region Routes*/}
            <Route path={regionPath}>
              <Route path={`/${regionPath}`} element={<HomePage/>}/>
              <Route path="info" element={<ProgramPage/>}/>
              <Route path="faculty" element={<FacultyPage/>}/>
              <Route path="faculty/:employeeId" element={<FacultyDetailPage/>}/>
              <Route path="employee-directory" element={<EmployeeDirectoryPage/>}/>
              <Route path="institute-hubs" element={<HubsPage/>}/>
            </Route>
            {/*Area Routes*/}
            <Route path={areaPath}>
              <Route path={`/${areaPath}`} element={<HomePage/>}/>
              <Route path="info" element={<ProgramPage/>}/>
              <Route path="faculty" element={<FacultyPage/>}/>
              <Route path="faculty/:employeeId" element={<FacultyDetailPage/>}/>
              <Route path="employee-directory" element={<EmployeeDirectoryPage />} />
              <Route path="institute-hubs" element={<HubsPage/>}/>
            </Route>

            {/*Root level routs*/}
            <Route index element={<HomePage/>}/>
            <Route path="/help-center" element={<HelpCenterPage/>}/>
            <Route path="/user-alerts" element={<UserAlertsPage alerts={alerts}/>}/>
            <Route path="/admin/system-alerts" element = {<SystemAlertsPage/>}/>

            <Route path="/admin/course-management" element={<CourseManagmentPage/>}/>
            <Route
              path="*"
              element={suppressNotFound ? <LoadingSpinner large={true} smartHideText={true}/> : <NotFoundPage />}
            />
          </Routes>
        </ErrorBoundary>

      </AppRoutePropertiesContext.Provider>
    </Suspense>);
}

export {AppRoutePropertiesContext}
export default AppRoutes;
